import { LogTypes } from '@audibene/ta-browser-tracker'
import { DeliveryModel } from '@audibene/ta.utils-opportunity'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  styled,
} from '@mui/material'
import { DatePickerComponent, DateTimePickerComponent } from '@syncfusion/ej2-react-calendars'
import { RecurrenceEditorChangeEventArgs, RecurrenceEditorComponent } from '@syncfusion/ej2-react-schedule'
import { useResourceAppointmentTypes } from 'hooks/useResourceAppointmentTypes'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { logRocket } from 'startup'
import { AppointmentType, AvailabilityType, EventType, teleaudiologyDeliveryModels, ToastSubType } from '../../../constants'
import { useAuth, useAvailabilityConstraintsQueryName, useEventTracking, useNotification, useResource, useShop } from '../../../hooks'
import { useAppointmentsService } from '../../../hooks/services'
import { theme } from '../../../modules/App/Providers/Theme/theme'
import { AvailabilityConstraintUpsertRequest } from '../../../types'
import { appointmentTypeOptions, omit } from '../../../util'
import { allowedDeliveryModelsToArray } from '../../../util/deliveryModelToDisplayText'
import { availabilityTheme, MINIMAL_TIME_BLOCK_IN_MINUTES } from '../constants'
import type { DtoCalendarConstraint } from '../types'
import { CheckboxLabel, DateContainer, DateInputContainer, DateLabel, DeliveryModelFormLabel } from './index.styles'
import { availabilityConstraintUpsertSchema } from './validationSchema'
import { useRecurrenceEditorStyle } from './useRecurrenceEditorStyle'
import { availabilityTypeToEventType, calendarConstraintEventToAvailabilityConstraintUpsertRequest, DtoConstraint } from './utils'

interface Props {
  constraint: DtoCalendarConstraint
  isNew?: boolean
  isOpen: boolean
  onClose: () => void
}

const DeleteButton = styled(LoadingButton)`
  color: ${theme.palette.error.main};
  left: 0.5rem;
  position: absolute;
`

const Container = styled('div')`
  align-items: center;
  display: grid;
  gap: 1rem;
  grid-template-columns: 25% 1fr 1fr 25%;
  grid-template-rows: auto;
`

const hasAnyTeleaudiologyDeliveryModel = (deliveryModels: DeliveryModel[]): boolean =>
  deliveryModels.some((deliveryModel) => teleaudiologyDeliveryModels.has(deliveryModel))

const ConstraintModal = ({ constraint: inputConstraint, isNew, isOpen, onClose }: Props) => {
  const queryClient = useQueryClient()
  const { resource } = useResource()
  const { appointmentTypes: availableAppointmentTypesPerDeliveryModel, isLoading: areAppointmentTypesLoading } =
    useResourceAppointmentTypes(resource?.id)
  const { t } = useTranslation()
  const { shop } = useShop()
  const { user } = useAuth()
  const endDateEditor = useRef(null)
  const startDateEditor = useRef(null)
  const { createAvailabilityConstraint, deleteAvailabilityConstraint, updateAvailabilityConstraint } = useAppointmentsService()
  const [constraint, setConstraint] = useState<DtoConstraint>(inputConstraint)
  const [deliveryModelsSelected, setDeliveryModelsSelected] = useState<DeliveryModel[]>([])
  const [isDeleting, beDeleting] = useState(false)
  const [isSaving, beSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, boolean>>({})
  const { notify } = useNotification()
  const { track: trackEvent } = useEventTracking()
  const recurrenceRef = useRecurrenceEditorStyle()

  const isWorking = isDeleting || isSaving

  const areAppointmentTypesMissingForTeleaudiology = useMemo(
    () =>
      Boolean(
        !areAppointmentTypesLoading &&
          hasAnyTeleaudiologyDeliveryModel(resource?.delivery_models || []) &&
          availableAppointmentTypesPerDeliveryModel?.TELEAUDIOLOGY?.length === 0
      ),
    [areAppointmentTypesLoading, resource?.delivery_models, availableAppointmentTypesPerDeliveryModel]
  )

  useEffect(() => {
    if (!isOpen) return

    if (areAppointmentTypesMissingForTeleaudiology) {
      const message = t('pages.settings.resources.availabilityConstraints.modal.noAvailableAppointmentTypes')
      notify({ message, type: ToastSubType.NO_AVAILABLE_APPOINTMENT_TYPES_FOUND }, 'warning', { autoHideDuration: 5000 })
    }
    // notify left out of dependency array to avoid showing the toast twice
  }, [isOpen, areAppointmentTypesMissingForTeleaudiology, t])

  const appointmentTypes = useMemo(() => {
    if (!availableAppointmentTypesPerDeliveryModel) {
      return []
    }

    const availableAppointmentTypes = deliveryModelsSelected
      .map((deliveryModel) => availableAppointmentTypesPerDeliveryModel[deliveryModel])
      .flat()
    return appointmentTypeOptions.filter((opt) => availableAppointmentTypes.includes(opt.value))
  }, [availableAppointmentTypesPerDeliveryModel, deliveryModelsSelected])

  useEffect(() => {
    if (!constraint || !constraint.appointmentTypes) return

    setConstraint({
      ...constraint,
      appointmentTypes: constraint.appointmentTypes.filter((appointmentType) =>
        appointmentTypes.some((at) => at.value === appointmentType)
      ),
    })
  }, [appointmentTypes])

  const onCloseProxy = useCallback(() => {
    if (!isWorking && onClose) {
      setErrors({})
      onClose()
    }
  }, [isWorking, onClose])

  const {
    availabilityType,
    appointmentTypes: tmpAppointmentTypes = [] as AppointmentType[],
    dateEnd,
    recurrenceId,
    recurrenceFrequency: recurrenceRule,
    dateStart: startTime,
  } = constraint ?? ({} as DtoCalendarConstraint)

  const isAvailableConstraint = availabilityType === AvailabilityType.AVAILABLE
  const selectedAppointmentTypes = isAvailableConstraint ? tmpAppointmentTypes : []
  const isOccurrence = Boolean(recurrenceId)

  const validateUpsertRequest = (request: AvailabilityConstraintUpsertRequest, isUpdate = false) => {
    const { error, value } = availabilityConstraintUpsertSchema.validate({ ...request, isNew: !isUpdate }, { abortEarly: false })

    const errors = error?.details?.reduce(
      (acc, { context }) => ({
        ...acc,
        [context?.key as string]: true,
      }),
      {}
    )

    const hasErrors = Object.keys(errors ?? {}).length > 0

    setErrors(errors ?? {})

    if (hasErrors) {
      console.error('Create request is invalid\n', error)

      throw new Error('Create request is invalid')
    }

    return value as AvailabilityConstraintUpsertRequest | undefined
  }

  useEffect(() => {
    setConstraint(inputConstraint)
    setDeliveryModelsSelected(inputConstraint?.deliveryModels ?? [])
  }, [inputConstraint])

  let title = ''

  const deleteErrorMessage = t(
    isAvailableConstraint
      ? `pages.settings.resources.availabilityConstraints.modal.availableTime.deleteErrorMessage`
      : `pages.settings.resources.availabilityConstraints.modal.blockedTime.deleteErrorMessage`
  )

  const deleteSuccessMessage = t(
    isAvailableConstraint
      ? `pages.settings.resources.availabilityConstraints.modal.availableTime.deleteSuccessMessage`
      : `pages.settings.resources.availabilityConstraints.modal.blockedTime.deleteSuccessMessage`
  )

  const saveErrorMessage = t(
    isAvailableConstraint
      ? `pages.settings.resources.availabilityConstraints.modal.availableTime.saveErrorMessage`
      : `pages.settings.resources.availabilityConstraints.modal.blockedTime.saveErrorMessage`
  )

  const saveSuccessMessage = t(
    isAvailableConstraint
      ? `pages.settings.resources.availabilityConstraints.modal.availableTime.saveSuccessMessage`
      : `pages.settings.resources.availabilityConstraints.modal.blockedTime.saveSuccessMessage`
  )

  switch (true) {
    case isNew && isAvailableConstraint:
      title = 'New Available Time'
      break
    case !isNew && isAvailableConstraint && isOccurrence:
      title = 'Edit Available Time (Occurrence)'
      break
    case !isNew && isAvailableConstraint:
      title = 'Edit Available Time'
      break
    case isNew && !isAvailableConstraint:
      title = 'New Blocked Time'
      break
    case !isNew && !isAvailableConstraint && isOccurrence:
      title = 'Edit Blocked Time (Occurrence)'
      break
    case !isNew && !isAvailableConstraint:
      title = 'Edit Blocked Time'
      break
    default:
      break
  }

  const onAppointmentTypesChange = (event: any) => {
    setErrors({ ...errors, appointment_types: false })
    setConstraint({ ...constraint, appointmentTypes: event.target.value ? event.target.value : [] })
  }

  const onRecurrenceChange = (event: RecurrenceEditorChangeEventArgs) => {
    setErrors({ ...errors, recurrence_rule: false })
    setConstraint({ ...constraint, recurrenceFrequency: event.value ? event.value : undefined })
  }

  const onEndTimeChange = (event: any) => {
    if (event.value) {
      event.value.setDate(startTime?.getDate())
    }
    setConstraint({ ...constraint, dateEnd: event.value ? event.value : undefined })
  }

  const onStartTimeChange = (event: any) => {
    if (dateEnd) {
      // this is weird
      dateEnd.setFullYear(event.value?.getFullYear())
      dateEnd.setMonth(event.value?.getMonth())
      dateEnd.setDate(event.value?.getDate())
      setConstraint({ ...constraint, dateEnd: event.value })
    } else {
      setConstraint({ ...constraint, dateEnd: event.value })
    }

    setConstraint({ ...constraint, dateStart: event.value ? event.value : undefined })
  }

  const onAllDay = () => setConstraint({ ...constraint, isAllDay: !constraint.isAllDay })

  const handleOnDelete = async () => {
    try {
      beDeleting(true)
      await deleteAvailabilityConstraint({ id: constraint.id as string, resourceId: resource?.id as string })
      await queryClient.refetchQueries(useAvailabilityConstraintsQueryName)

      const eventName = availabilityTypeToEventType('delete', constraint.availabilityType)

      trackEvent(eventName, {
        data: { resourceIds: [resource?.id as string], shopId: shop?.id, userRoles: user?.roles, userId: user?.account_id },
      })

      onClose()
      notify({ message: deleteSuccessMessage, type: ToastSubType.SUCCESS }, 'success')
    } catch (error) {
      notify({ message: deleteErrorMessage, type: ToastSubType.UNHANDLED_ERROR }, 'error')
      logRocket.log(LogTypes.ERROR, 'Error while deleting availability constraint', { error })
    } finally {
      beDeleting(false)
    }
  }

  const handleOnSave = async () => {
    const resourceId = resource?.id as string
    const request = calendarConstraintEventToAvailabilityConstraintUpsertRequest(constraint)

    let actualRequest: AvailabilityConstraintUpsertRequest | undefined

    try {
      actualRequest = validateUpsertRequest(request) as AvailabilityConstraintUpsertRequest
    } catch {
      // Do nothing
      return
    }

    let eventName: EventType
    try {
      beSaving(true)

      if (isNew) {
        await createAvailabilityConstraint(resourceId, omit(actualRequest as AvailabilityConstraintUpsertRequest, ['isNew']))
        eventName = availabilityTypeToEventType('create', constraint.availabilityType)
      } else {
        await updateAvailabilityConstraint(
          resourceId,
          constraint.id as string,
          omit(actualRequest as AvailabilityConstraintUpsertRequest, ['availability_type', 'isNew'])
        )
        eventName = availabilityTypeToEventType('update', constraint.availabilityType)
      }
      await queryClient.refetchQueries(useAvailabilityConstraintsQueryName)
      trackEvent(eventName, {
        data: { resourceIds: [resourceId], shopId: shop?.id, userRoles: user?.roles, userId: user?.account_id },
      })
      onClose()
      notify({ message: saveSuccessMessage, type: ToastSubType.SUCCESS }, 'success')
    } catch (error) {
      notify({ message: saveErrorMessage, type: ToastSubType.UNHANDLED_ERROR }, 'error')
      logRocket.log(LogTypes.ERROR, 'Error while saving availability constraint', { error })
    } finally {
      beSaving(false)
      setDeliveryModelsSelected([])
    }
  }

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) {
      return
    }

    return appointmentTypes
      .filter(({ value }) => selectedAppointmentTypes.includes(value))
      .map(({ name }) => name)
      .join(', ')
  }

  const selectedAppointmentTypesDisplayText = appointmentTypes
    .filter(({ value }) => selectedAppointmentTypes.includes(value))
    .map(({ name }) => name)
    .join(', ')

  const handleDeliveryModelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target
    let deliveryModels: DeliveryModel[] = []
    if (checked) {
      deliveryModels = [...deliveryModelsSelected, name as DeliveryModel]
    } else {
      deliveryModels = deliveryModelsSelected.filter((deliveryModel) => deliveryModel !== name) ?? []
    }
    setDeliveryModelsSelected(deliveryModels)
    setConstraint({
      ...constraint,
      deliveryModels,
    })
  }

  useEffect(() => {
    const endDateEditorGrandParent = endDateEditor?.current as unknown as HTMLElement
    const startDateEditorGrandParent = startDateEditor?.current as unknown as HTMLElement

    if (startDateEditorGrandParent) {
      const clearEditor = startDateEditorGrandParent.getElementsByClassName('e-clear-icon')[0] as HTMLElement

      clearEditor.style.display = 'none'
    }

    if (endDateEditorGrandParent) {
      const timeEditor = endDateEditorGrandParent.getElementsByClassName('e-input-group-icon e-time-icon e-icons')[0] as HTMLElement
      const timeInput = endDateEditorGrandParent.getElementsByClassName(
        'e-control e-datetimepicker e-lib e-input e-keyboard'
      )[0] as HTMLElement
      const clearEditor = endDateEditorGrandParent.getElementsByClassName('e-clear-icon')[0] as HTMLElement
      const datePicker = endDateEditorGrandParent.getElementsByClassName('e-input-group-icon e-date-icon e-icons')[0] as HTMLElement

      timeInput.style.caretColor = 'transparent'

      clearEditor.style.display = 'none'
      datePicker.style.display = 'none'

      timeInput?.addEventListener('focus', () => {
        timeEditor.dispatchEvent(new Event('mousedown'))
      })

      timeInput?.addEventListener('mousedown', () => {
        timeEditor.dispatchEvent(new Event('mousedown'))
      })

      timeInput?.addEventListener('keydown', (e) => {
        timeInput.style.color = 'red'
        e.preventDefault()
      })
      timeInput?.addEventListener('keyup', (e) => {
        timeInput.style.color = 'black'
        e.preventDefault()
      })
    }
  }, [endDateEditor.current, startDateEditor.current])

  return (
    <Dialog
      aria-labelledby="form-dialog-title"
      open={isOpen}
      onClose={onCloseProxy}
      PaperProps={{ style: { border: `2px solid ${availabilityTheme[availabilityType]?.accent}`, minWidth: '37.75rem' } }}
    >
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <DialogContent dividers>
        <Container>
          <FormControl
            error={Boolean(errors['delivery_models'])}
            style={{ gridColumn: '1' }}
            sx={{ m: 0, width: '34.75rem' }}
            variant="standard"
          >
            <DeliveryModelFormLabel id="delivery-model-group-label">{t('common.label.deliveryModel')}</DeliveryModelFormLabel>
            <FormGroup aria-labelledby="delivery-model-group-label" row>
              {allowedDeliveryModelsToArray(resource?.delivery_models || []).map(({ key, value }) => (
                <FormControlLabel
                  control={<Checkbox checked={deliveryModelsSelected.includes(key)} name={key} onChange={handleDeliveryModelChange} />}
                  key={key}
                  label={<CheckboxLabel>{value}</CheckboxLabel>}
                />
              ))}
            </FormGroup>
          </FormControl>
          {isAvailableConstraint && (
            <FormControl
              error={Boolean(errors['appointment_types'])}
              style={{ gridColumn: '1' }}
              sx={{ m: 0, width: '34.75rem' }}
              variant="standard"
            >
              <InputLabel id="appointment-types-label">{t('common.label.appointmentTypes')}</InputLabel>
              <Select
                displayEmpty
                fullWidth
                id="appointment-types"
                labelId="appointment-types-label"
                multiple
                onChange={onAppointmentTypesChange}
                renderValue={renderValue}
                required
                value={selectedAppointmentTypes}
              >
                <MenuItem disabled value="">
                  <span>{t('common.label.selectedAppointmentTypes')}</span>
                </MenuItem>
                {appointmentTypes.map(({ name, value }) => (
                  <MenuItem key={value} value={value}>
                    <Checkbox checked={Boolean(selectedAppointmentTypes && selectedAppointmentTypes.indexOf(value) > -1)} />
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
              {Boolean(errors['appointment_types']) && <FormHelperText color="error">{selectedAppointmentTypesDisplayText}</FormHelperText>}
            </FormControl>
          )}
          {constraint && constraint.isAllDay ? (
            <DateContainer style={{ gridColumn: '1 / 3' }}>
              <DateLabel>
                {t('pages.settings.resources.availabilityConstraints.modal.allDayStartDate')}
                <DateInputContainer>
                  <DatePickerComponent id="date" onChange={onStartTimeChange} value={startTime} />
                </DateInputContainer>
              </DateLabel>
            </DateContainer>
          ) : (
            <>
              <DateContainer style={{ gridColumn: '1 / 3' }} ref={startDateEditor}>
                <DateLabel>
                  {t('pages.settings.resources.availabilityConstraints.modal.startTime')}
                  <DateInputContainer>
                    <DateTimePickerComponent
                      id="date-start"
                      onChange={onStartTimeChange}
                      step={MINIMAL_TIME_BLOCK_IN_MINUTES}
                      value={startTime}
                    />
                  </DateInputContainer>
                </DateLabel>
              </DateContainer>
              <DateContainer style={{ gridColumn: '3 / 5' }} ref={endDateEditor}>
                <DateLabel>
                  {t('pages.settings.resources.availabilityConstraints.modal.endTime')}
                  <DateInputContainer>
                    <DateTimePickerComponent
                      id="date-end"
                      onChange={onEndTimeChange}
                      step={MINIMAL_TIME_BLOCK_IN_MINUTES}
                      value={dateEnd}
                    />
                  </DateInputContainer>
                </DateLabel>
              </DateContainer>
            </>
          )}

          {!isAvailableConstraint && (
            <FormControlLabel
              style={{ gridColumn: '1 / 5' }}
              control={<Checkbox checked={Boolean(constraint && constraint.isAllDay)} onChange={onAllDay} />}
              label={<CheckboxLabel>{t('pages.settings.resources.availabilityConstraints.modal.allDay')}</CheckboxLabel>}
            />
          )}

          <RecurrenceEditorComponent
            change={onRecurrenceChange}
            id="recurrence-rule"
            ref={recurrenceRef}
            style={{ gridColumn: '1 / 5' }}
            value={recurrenceRule}
          ></RecurrenceEditorComponent>
        </Container>
      </DialogContent>
      <DialogActions>
        {!isNew && (
          <DeleteButton color="error" disabled={isWorking} loading={isDeleting} onClick={handleOnDelete}>
            {t('common.delete')}
          </DeleteButton>
        )}
        <Button disabled={isWorking} onClick={onCloseProxy}>
          {t('common.cancel')}
        </Button>
        <LoadingButton color="primary" disabled={isWorking} loading={isSaving} onClick={handleOnSave} type="submit" variant="contained">
          {t('common.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export { ConstraintModal }
